import { createFileRoute } from '@tanstack/react-router'
import { ProjectBoard } from '~/components/projects/project-board'
import { PROJECT_HEALTH_ORDER, type ProjectHealthFilter } from '~/lib/project-boards'
import {
  PROJECT_VIEWS,
  type ProjectType,
  type ProjectView,
  TYPE_ORDER,
  USAGE_ORDER,
  type UsageType,
} from '~/lib/project-enums'
import {
  PROJECT_FIELDS,
  PROJECT_GROUP_BYS,
  PROJECT_SORT_KEYS,
  type ProjectField,
  type ProjectGroupBy,
  type ProjectSort,
  type ProjectSortKey,
} from '~/lib/project-fields'
import { enumArray, optionalBoolean, optionalString, stringArray } from '~/lib/search-params'
import type { Id } from '~convex/_generated/dataModel'

export type ProjectsSearch = {
  /** Selected view; absent is treated as the default `'board'`. */
  view?: ProjectView
  /** Full-text search query (matches name + client name). */
  q?: string
  contacts?: Id<'contacts'>[]
  projectTypes?: ProjectType[]
  usageTypes?: UsageType[]
  /**
   * Status and stage KEYS. Both are workspace-authored — a custom status, a
   * per-type template stage — so neither has a whitelist to validate against
   * here. An unknown key survives the decoder and simply matches nothing,
   * which is what a filter for a status that was deleted should do.
   */
  statuses?: string[]
  stages?: string[]
  /** Whose projects — the column's "Meus projetos" row writes this. */
  owners?: Id<'users'>[]
  /** The card marks, as a filter: what the column's Atrasados / Parados do. */
  health?: ProjectHealthFilter[]
  /** List-mode presentation. Absent means the defaults in `project-fields`. */
  group?: ProjectGroupBy
  sort?: ProjectSortKey
  direction?: ProjectSort['direction']
  fields?: ProjectField[]
  /** When true, archived projects appear in both views. */
  archived?: boolean
  /** Sidebar archive destination: show archived projects and nothing else. */
  archivedOnly?: boolean
  /** Sidebar board destination for projects whose type is absent. */
  withoutType?: boolean
}

const projectView = (raw: unknown): ProjectView | undefined =>
  PROJECT_VIEWS.find((value) => value === raw)

export const Route = createFileRoute('/_authenticated/$workspaceSlug/projects/')({
  component: ProjectsIndex,
  validateSearch: (search: Record<string, unknown>): ProjectsSearch => ({
    archived: optionalBoolean(search.archived),
    archivedOnly: optionalBoolean(search.archivedOnly),
    contacts: stringArray<Id<'contacts'>>(search.contacts),
    direction:
      search.direction === 'asc' || search.direction === 'desc' ? search.direction : undefined,
    fields: enumArray<ProjectField>(search.fields, PROJECT_FIELDS),
    group: PROJECT_GROUP_BYS.find((value) => value === search.group),
    health: enumArray<ProjectHealthFilter>(search.health, PROJECT_HEALTH_ORDER),
    owners: stringArray<Id<'users'>>(search.owners),
    projectTypes: enumArray<ProjectType>(search.projectTypes, TYPE_ORDER),
    q: optionalString(search.q),
    sort: PROJECT_SORT_KEYS.find((value) => value === search.sort),
    stages: stringArray(search.stages),
    statuses: stringArray(search.statuses),
    usageTypes: enumArray<UsageType>(search.usageTypes, USAGE_ORDER),
    view: projectView(search.view),
    withoutType: optionalBoolean(search.withoutType),
  }),
})

function ProjectsIndex() {
  return <ProjectBoard />
}
